import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    ChevronLeft,
    ChevronRight,
    CheckCircle2,
    Save,
    CalendarCheck,
    Info,
    Sparkles,
    XCircle,
} from "lucide-react";

const FRENCH_MONTHS = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const FRENCH_WEEKDAYS_SHORT = ["L", "M", "M", "J", "V", "S", "D"];
const FRENCH_WEEKDAYS_LONG = [
    "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche",
];

// Build array of days for a given month/year
function buildMonthGrid(year, month) {
    const firstOfMonth = new Date(year, month, 1);
    // JS: 0 = Sunday, we want Monday-first
    const firstWeekday = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
}

function isoDate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

// Monday=0 … Sunday=6 (aligned with the UI order)
function jsDayToMondayFirst(date) {
    return (date.getDay() + 6) % 7;
}

// Default cursor: today
const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0);
const MAX_DATE = new Date("2027-12-31T00:00:00");
const MAX_DATE_ISO = isoDate(MAX_DATE);
const MAX_DATE_LABEL = "31 décembre 2027";

// Compute total business-day-independent slots between TODAY and MAX_DATE
const TOTAL_DAYS_HORIZON = Math.floor((MAX_DATE - TODAY) / 86400000) + 1;

export default function DisponibilitesCalendar({ mode = "edit", onSubmitted }) {
    const [cursor, setCursor] = useState({ year: TODAY.getFullYear(), month: TODAY.getMonth() });
    // Set of ISO dates that the formateur is available on (full day only).
    const [selected, setSelected] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/formateur/disponibilites")
            .then((r) => {
                const s = new Set();
                (r.data?.slots || []).forEach((slot) => s.add(slot.date));
                setSelected(s);
            })
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);

    const grid = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);

    const goPrev = () => {
        let { year, month } = cursor;
        month -= 1;
        if (month < 0) { month = 11; year -= 1; }
        if (year < TODAY.getFullYear() || (year === TODAY.getFullYear() && month < TODAY.getMonth())) return;
        setCursor({ year, month });
    };
    const goNext = () => {
        let { year, month } = cursor;
        month += 1;
        if (month > 11) { month = 0; year += 1; }
        if (year > 2027) return;
        setCursor({ year, month });
    };

    const toggleDay = (d) => {
        if (!d || d < TODAY || d > MAX_DATE) return;
        const iso = isoDate(d);
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(iso)) next.delete(iso);
            else next.add(iso);
            return next;
        });
    };

    // Compute how many upcoming occurrences of each weekday exist between
    // TODAY and MAX_DATE, and how many are already selected.
    const weekdayStats = useMemo(() => {
        const stats = FRENCH_WEEKDAYS_LONG.map(() => ({ total: 0, selected: 0 }));
        for (let t = TODAY.getTime(); t <= MAX_DATE.getTime(); t += 86400000) {
            const d = new Date(t);
            const wd = jsDayToMondayFirst(d);
            stats[wd].total += 1;
            if (selected.has(isoDate(d))) stats[wd].selected += 1;
        }
        return stats;
    }, [selected]);

    // Toggle all upcoming instances of a given weekday. If everything is already
    // selected → deselect everything. Otherwise → add every missing occurrence.
    const toggleAllWeekday = (wdIndex) => {
        const st = weekdayStats[wdIndex];
        const allSelected = st.total > 0 && st.selected === st.total;
        setSelected((prev) => {
            const next = new Set(prev);
            for (let t = TODAY.getTime(); t <= MAX_DATE.getTime(); t += 86400000) {
                const d = new Date(t);
                if (jsDayToMondayFirst(d) !== wdIndex) continue;
                const iso = isoDate(d);
                if (allSelected) next.delete(iso);
                else next.add(iso);
            }
            return next;
        });
        const wd = FRENCH_WEEKDAYS_LONG[wdIndex];
        toast.success(
            allSelected
                ? `Tous les ${wd.toLowerCase()}s ont été retirés.`
                : `Tous les ${wd.toLowerCase()}s (${st.total}) jusqu'au ${MAX_DATE_LABEL} sont sélectionnés.`,
        );
    };

    const clearAll = () => {
        if (selected.size === 0) return;
        setSelected(new Set());
        toast.success("Toutes les disponibilités ont été effacées.");
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                slots: Array.from(selected)
                    .sort()
                    .map((date) => ({ date, period: "full_day" })),
            };
            await api.put("/formateur/disponibilites", payload);
            toast.success("Disponibilités enregistrées.");
            onSubmitted?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="py-20 text-center text-brand-smoke">Chargement…</div>;

    return (
        <div className="space-y-6" data-testid="disponibilites-calendar">
            {/* ============ Bandeau de contexte — Explicite ============ */}
            <section
                className="rounded-3xl border border-brand-orange/30 bg-brand-orange/[0.06] p-6 sm:p-8"
                data-testid="dispo-context-banner"
            >
                <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-brand-orange text-white">
                        <CalendarCheck className="h-5 w-5" />
                    </div>
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-orange">
                            Vos disponibilités jusqu&apos;au {MAX_DATE_LABEL}
                        </div>
                        <h2 className="mt-1 font-display text-2xl leading-tight text-brand-ink sm:text-3xl">
                            Cochez <em className="italic text-brand-orange">tous</em> les jours où vous pourriez animer une session.
                        </h2>
                        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-brand-ink/75">
                            Ces dates nous servent uniquement à <strong>construire le planning</strong> — cocher un jour
                            ne signifie pas qu&apos;une session vous sera automatiquement assignée à cette date.
                            <strong className="ml-1 text-brand-ink">Plus vous cochez, plus nous avons de flexibilité pour vous proposer des sessions.</strong>
                        </p>
                    </div>
                </div>
            </section>

            {/* ============ Sélection rapide par jour de semaine ============ */}
            <section
                className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium"
                data-testid="dispo-weekday-shortcuts"
            >
                <div className="flex items-start gap-3">
                    <Sparkles className="mt-1 h-5 w-5 flex-shrink-0 text-brand-orange" />
                    <div className="flex-1">
                        <div className="font-display text-lg text-brand-ink">Sélection rapide — tous les…</div>
                        <p className="mt-1 text-sm text-brand-smoke">
                            Cliquez sur un jour de la semaine pour cocher (ou décocher) <strong>toutes</strong> les
                            occurrences jusqu&apos;au {MAX_DATE_LABEL}.
                        </p>
                    </div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-7">
                    {FRENCH_WEEKDAYS_LONG.map((label, idx) => {
                        const st = weekdayStats[idx];
                        const allChecked = st.total > 0 && st.selected === st.total;
                        const partial = st.selected > 0 && st.selected < st.total;
                        return (
                            <button
                                key={label}
                                type="button"
                                onClick={() => toggleAllWeekday(idx)}
                                data-testid={`dispo-weekday-${label.toLowerCase()}`}
                                className={`rounded-2xl border p-3 text-left transition-all ${
                                    allChecked
                                        ? "border-brand-orange bg-brand-orange text-white shadow-[0_4px_16px_rgba(232,135,60,0.25)]"
                                        : partial
                                          ? "border-brand-orange bg-brand-orange/10 text-brand-ink"
                                          : "border-brand-line bg-white text-brand-ink hover:border-brand-orange"
                                }`}
                            >
                                <div className="text-xs font-semibold uppercase tracking-wider opacity-70">
                                    Tous les
                                </div>
                                <div className="mt-1 font-display text-lg">{label}s</div>
                                <div className={`mt-2 text-[11px] ${allChecked ? "text-white/85" : "text-brand-smoke"}`}>
                                    {st.selected}/{st.total} cochés
                                </div>
                            </button>
                        );
                    })}
                </div>
                {selected.size > 0 && (
                    <div className="mt-4 flex justify-end">
                        <button
                            type="button"
                            onClick={clearAll}
                            data-testid="dispo-clear-all"
                            className="inline-flex items-center gap-1.5 rounded-full border border-brand-line px-4 py-1.5 text-xs font-semibold text-brand-ink/70 transition-colors hover:border-red-300 hover:text-red-600"
                        >
                            <XCircle className="h-3.5 w-3.5" /> Tout effacer
                        </button>
                    </div>
                )}
            </section>

            <div className="grid gap-8 lg:grid-cols-12">
                {/* ============ Calendrier ============ */}
                <div className="lg:col-span-8">
                    <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium md:p-8">
                        <div className="flex items-center justify-between">
                            <button
                                onClick={goPrev}
                                data-testid="calendar-prev"
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange hover:text-brand-orange"
                                aria-label="Mois précédent"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <div
                                className="font-display text-2xl text-brand-ink"
                                data-testid="calendar-month-label"
                            >
                                {FRENCH_MONTHS[cursor.month]} {cursor.year}
                            </div>
                            <button
                                onClick={goNext}
                                data-testid="calendar-next"
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange hover:text-brand-orange"
                                aria-label="Mois suivant"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="mt-6 grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                            {FRENCH_WEEKDAYS_SHORT.map((w, i) => (
                                <div key={i} className="py-2">{w}</div>
                            ))}
                        </div>

                        <div className="mt-1 grid grid-cols-7 gap-1">
                            {grid.map((cell, i) => {
                                if (!cell) return <div key={i} className="aspect-square" />;
                                const iso = isoDate(cell);
                                const disabled = cell < TODAY || cell > MAX_DATE;
                                const isSelected = selected.has(iso);
                                return (
                                    <button
                                        type="button"
                                        key={iso}
                                        onClick={() => toggleDay(cell)}
                                        disabled={disabled}
                                        data-testid={`calendar-day-${iso}`}
                                        className={`aspect-square w-full rounded-xl border text-sm transition-all ${
                                            disabled
                                                ? "border-transparent text-brand-line cursor-not-allowed"
                                                : isSelected
                                                  ? "border-brand-orange bg-brand-orange text-white shadow-[0_4px_12px_rgba(232,135,60,0.25)]"
                                                  : "border-brand-line hover:border-brand-orange hover:bg-brand-orange/5"
                                        }`}
                                    >
                                        <div className="font-medium">{cell.getDate()}</div>
                                        {isSelected && (
                                            <div className="mt-0.5 text-[9px] uppercase tracking-wider">
                                                Dispo
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-6 flex items-start gap-2 text-xs text-brand-smoke">
                            <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-brand-orange" />
                            <span>
                                Chaque case sélectionnée = <strong className="text-brand-ink">journée entière</strong>{" "}
                                de disponibilité. Cliquez sur un jour pour l&apos;ajouter ou le retirer.
                            </span>
                        </div>
                    </div>
                </div>

                {/* ============ Sidebar récap ============ */}
                <aside className="lg:col-span-4">
                    <div className="sticky top-6 space-y-4">
                        <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium">
                            <div className="flex items-center gap-3">
                                <CalendarCheck className="h-5 w-5 text-brand-orange" />
                                <div>
                                    <div
                                        className="font-display text-4xl text-brand-ink leading-none"
                                        data-testid="dispo-total"
                                    >
                                        {selected.size}
                                    </div>
                                    <div className="mt-1 text-xs uppercase tracking-wider text-brand-smoke">
                                        Journée{selected.size > 1 ? "s" : ""} sélectionnée{selected.size > 1 ? "s" : ""}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 border-t border-brand-line pt-4 text-xs leading-relaxed text-brand-ink/70">
                                sur {TOTAL_DAYS_HORIZON} jours possibles jusqu&apos;au{" "}
                                <strong className="text-brand-ink">{MAX_DATE_LABEL}</strong>.
                            </div>
                        </div>

                        <div className="rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-5 text-sm">
                            <div className="flex items-start gap-2">
                                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" />
                                <div>
                                    <div className="font-semibold text-brand-ink">
                                        Un doute ? Cochez plus large.
                                    </div>
                                    <p className="mt-1.5 text-xs leading-relaxed text-brand-ink/70">
                                        Ces dispos ne sont <strong>pas des engagements</strong> : nous vous
                                        recontacterons systématiquement pour confirmer avant de programmer une session.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={save}
                            disabled={saving}
                            data-testid="dispo-submit"
                            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#d9772c] disabled:opacity-60"
                        >
                            {saving ? "Enregistrement…" : (
                                <>
                                    <Save className="h-4 w-4" />
                                    {mode === "onboarding" ? "Valider mes disponibilités" : "Enregistrer mes disponibilités"}
                                </>
                            )}
                        </button>
                    </div>
                </aside>
            </div>
        </div>
    );
}
